import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatEuro, formatData } from "@/lib/api";
import { StatoBadge, ScadenzaBadge } from "@/components/Badges";
import AssegnoDettaglio from "@/components/AssegnoDettaglio";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Search, FilePlus2, Archive as ArchiveIcon, CheckSquare, Trash2, X } from "lucide-react";

const FILTRI = [
  { key: "", label: "Tutti" },
  { key: "ricevuto", label: "Ricevuti" },
  { key: "girato", label: "Girati" },
  { key: "incassato", label: "Incassati" },
];

const Archivio = () => {
  const navigate = useNavigate();
  const [assegni, setAssegni] = useState([]);
  const [filtro, setFiltro] = useState("");
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(null);
  const [open, setOpen] = useState(false);
  const [selMode, setSelMode] = useState(false);
  const [selezionati, setSelezionati] = useState(new Set());

  const carica = (f) => api.lista(f || undefined).then(setAssegni).catch(() => {});

  useEffect(() => { carica(filtro); }, [filtro]);

  const filtrati = assegni.filter((a) => {
    if (!q) return true;
    const s = q.toLowerCase();
    return (
      (a.banca || "").toLowerCase().includes(s) ||
      (a.numero_assegno || "").toLowerCase().includes(s) ||
      (a.ricevuto_da || "").toLowerCase().includes(s) ||
      (a.destinatario || "").toLowerCase().includes(s)
    );
  });

  const toggleSel = (id) => {
    setSelezionati((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const tuttiSelezionati = filtrati.length > 0 && filtrati.every((a) => selezionati.has(a.id));
  const toggleTutti = () => {
    if (tuttiSelezionati) {
      setSelezionati(new Set());
    } else {
      setSelezionati(new Set(filtrati.map((a) => a.id)));
    }
  };

  const annullaSelezione = () => {
    setSelMode(false);
    setSelezionati(new Set());
  };

  const eliminaSelezionati = async () => {
    const ids = Array.from(selezionati);
    try {
      const res = await api.eliminaMultipli(ids);
      toast.success(`${res.eliminati} assegni eliminati`);
      annullaSelezione();
      carica(filtro);
    } catch {
      toast.error("Errore durante l'eliminazione");
    }
  };

  const onRowClick = (a) => {
    if (selMode) {
      toggleSel(a.id);
    } else {
      setSel(a);
      setOpen(true);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight">Archivio Assegni</h1>
          <p className="text-[#5C5F58] mt-1">Tutti gli assegni ricevuti, con tracciabilità completa</p>
        </div>
        <div className="flex gap-2">
          {!selMode ? (
            <Button
              variant="outline"
              onClick={() => setSelMode(true)}
              data-testid="btn-selezione-multipla"
              className="border-[#E5E2DC] bg-white hover:bg-[#F2F0EB] text-[#1A1C18]"
            >
              <CheckSquare className="w-4 h-4 mr-1.5" /> Selezione multipla
            </Button>
          ) : (
            <>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    disabled={selezionati.size === 0}
                    data-testid="btn-elimina-selezionati"
                    className="bg-[#B84223] hover:bg-[#9a3119] text-white disabled:opacity-50"
                  >
                    <Trash2 className="w-4 h-4 mr-1.5" /> Elimina ({selezionati.size})
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Eliminare {selezionati.size} assegni?</AlertDialogTitle>
                    <AlertDialogDescription>Questa azione è irreversibile.</AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Annulla</AlertDialogCancel>
                    <AlertDialogAction onClick={eliminaSelezionati} data-testid="btn-conferma-elimina-multipli" className="bg-[#B84223] hover:bg-[#9a3119]">
                      Elimina
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
              <Button variant="outline" onClick={annullaSelezione} data-testid="btn-annulla-selezione" className="border-[#E5E2DC] bg-white hover:bg-[#F2F0EB]">
                <X className="w-4 h-4 mr-1.5" /> Annulla
              </Button>
            </>
          )}
          <Button onClick={() => navigate("/nuovo")} data-testid="archivio-nuovo-btn" className="bg-[#1C3F35] hover:bg-[#132A23] text-white">
            <FilePlus2 className="w-4 h-4 mr-1.5" /> Nuovo Assegno
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div className="flex gap-2">
          {FILTRI.map((f) => (
            <button
              key={f.key}
              data-testid={`filtro-${f.key || "tutti"}`}
              onClick={() => setFiltro(f.key)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filtro === f.key ? "bg-[#1C3F35] text-white" : "bg-white border border-[#E5E2DC] text-[#5C5F58] hover:bg-[#F2F0EB]"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div className="relative w-full sm:w-72">
          <Search className="w-4 h-4 text-[#5C5F58] absolute left-3 top-1/2 -translate-y-1/2" />
          <Input data-testid="ricerca-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca banca, numero, persona..." className="pl-9 bg-white" />
        </div>
      </div>

      <div className="bg-white border border-[#E5E2DC] rounded-xl shadow-sm overflow-hidden">
        {filtrati.length === 0 ? (
          <div className="p-16 text-center">
            <div className="w-14 h-14 rounded-full bg-[#F2F0EB] flex items-center justify-center mx-auto mb-4">
              <ArchiveIcon className="w-7 h-7 text-[#5C5F58]" strokeWidth={1.5} />
            </div>
            <p className="font-heading font-semibold">Nessun assegno trovato</p>
            <p className="text-sm text-[#5C5F58] mt-1">Carica la foto del tuo primo assegno per iniziare.</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="border-[#E5E2DC]">
                {selMode && (
                  <TableHead className="w-10">
                    <Checkbox checked={tuttiSelezionati} onCheckedChange={toggleTutti} data-testid="checkbox-seleziona-tutti" />
                  </TableHead>
                )}
                <TableHead className="text-xs uppercase tracking-wider">Banca / N°</TableHead>
                <TableHead className="text-xs uppercase tracking-wider">Valore</TableHead>
                <TableHead className="text-xs uppercase tracking-wider">Ricevuto da</TableHead>
                <TableHead className="text-xs uppercase tracking-wider">Destinatario</TableHead>
                <TableHead className="text-xs uppercase tracking-wider">Scadenza</TableHead>
                <TableHead className="text-xs uppercase tracking-wider">Stato</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtrati.map((a) => (
                <TableRow
                  key={a.id}
                  data-testid="archivio-row"
                  onClick={() => onRowClick(a)}
                  className={`cursor-pointer border-[#F2F0EB] hover:bg-[#F9F8F6] ${selezionati.has(a.id) ? "bg-[#F2F0EB]" : ""}`}
                >
                  {selMode && (
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={selezionati.has(a.id)}
                        onCheckedChange={() => toggleSel(a.id)}
                        data-testid={`checkbox-row-${a.id}`}
                      />
                    </TableCell>
                  )}
                  <TableCell>
                    <p className="font-medium">{a.banca || "—"}</p>
                    <p className="text-xs text-[#5C5F58] font-mono-num">N° {a.numero_assegno || "—"}</p>
                  </TableCell>
                  <TableCell className="font-mono-num font-semibold">{formatEuro(a.valore)}</TableCell>
                  <TableCell>{a.ricevuto_da || "—"}</TableCell>
                  <TableCell>{a.destinatario || <span className="text-[#5C5F58]">—</span>}</TableCell>
                  <TableCell>
                    <div className="flex flex-col gap-1">
                      <span className="text-sm">{formatData(a.data_scadenza)}</span>
                      <ScadenzaBadge giorni={a.giorni_alla_scadenza} />
                    </div>
                  </TableCell>
                  <TableCell><StatoBadge stato={a.stato} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <AssegnoDettaglio
        assegno={sel}
        open={open}
        onOpenChange={setOpen}
        onUpdated={() => carica(filtro)}
        onDeleted={() => carica(filtro)}
      />
    </div>
  );
};

export default Archivio;
